import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import commissionService from '../../services/ShipmentCommissionService.js';
import { mlSheetService } from '../../services/mlSheetService.js';
import { DollarSign, ChevronDown, ChevronRight, MapPin, Send, Download } from 'lucide-react';
import { PageLayout, Button } from '../common/index.js';

const AddCommission = () => {
  const [formData, setFormData] = useState({
    ml_sheet_id: '',
    company_profile_id: '',
    vehicle_owner_id: '',
    company_commission_percent: 30,
    owner_commission_percent: 70,
  });
  const [mlSheets, setMlSheets] = useState([]);
  const [mlSearchQuery, setMlSearchQuery] = useState('');
  const [mlDropdownOpen, setMlDropdownOpen] = useState(false);
  const [mlPage, setMlPage] = useState(1);
  const ML_PAGE_SIZE = 5;
  const [companyProfiles, setCompanyProfiles] = useState([]);
  const [vehicleOwners, setVehicleOwners] = useState([]);
  const [preview, setPreview] = useState(null);
  const [hubData, setHubData] = useState(null);
  const [expandedHubs, setExpandedHubs] = useState({});
  const [activeHubTab, setActiveHubTab] = useState('sender');

  // Per-hub commission percentages: { 'HUB001': 5, 'HUB002': 3 }
  const [senderHubPercents, setSenderHubPercents] = useState({});
  const [receiverHubPercents, setReceiverHubPercents] = useState({});

  const [loading, setLoading] = useState(false);
  const [dataLoading, setDataLoading] = useState(true);
  const [mlLoading, setMlLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => { fetchInitialData(); }, []);

  useEffect(() => {
    const handleClickOutside = (e) => { if (!e.target.closest('.ml-sheet-dropdown')) setMlDropdownOpen(false); };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (formData.ml_sheet_id) {
      fetchMlSheetData(formData.ml_sheet_id);
    } else {
      setPreview(null);
      setHubData(null);
      setSenderHubPercents({});
      setReceiverHubPercents({});
    }
  }, [formData.ml_sheet_id]);

  const fetchInitialData = async () => {
    setDataLoading(true);
    try {
      const [mlSheetsRes, companiesRes, ownersRes] = await Promise.all([
        mlSheetService.getAllMLSheets(1, 1000, { all: true }),
        commissionService.getCompanyProfiles(),
        commissionService.getVehicleOwners()
      ]);
      setMlSheets(Array.isArray(mlSheetsRes.data?.data || mlSheetsRes.data) ? (mlSheetsRes.data?.data || mlSheetsRes.data) : []);
      setCompanyProfiles(Array.isArray(companiesRes.data?.data || companiesRes.data) ? (companiesRes.data?.data || companiesRes.data) : []);
      setVehicleOwners(Array.isArray(ownersRes.data?.data || ownersRes.data) ? (ownersRes.data?.data || ownersRes.data) : []);
    } catch (err) {
      Alert.error('Failed to load form data');
    } finally {
      setDataLoading(false);
    }
  };

  const fetchMlSheetData = async (mlSheetId) => {
    setMlLoading(true);
    setPreview(null);
    setHubData(null);
    setSenderHubPercents({});
    setReceiverHubPercents({});
    try {
      const [previewRes, hubsRes] = await Promise.all([
        commissionService.calculatePreview(mlSheetId),
        commissionService.getHubsByMlSheet(mlSheetId)
      ]);
      setPreview(previewRes.data?.data || null);
      const hData = hubsRes.data?.data || null;
      setHubData(hData);
      // Initialize per-hub percents to 0
      if (hData?.sender_hubs) {
        const sp = {};
        hData.sender_hubs.forEach(h => { sp[h.hub_id] = 0; });
        setSenderHubPercents(sp);
      }
      if (hData?.receiver_hubs) {
        const rp = {};
        hData.receiver_hubs.forEach(h => { rp[h.hub_id] = 0; });
        setReceiverHubPercents(rp);
      }
    } catch (err) {
      Alert.error('Failed to load shipment data for selected ML Sheet');
    } finally {
      setMlLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => {
      if (name === 'owner_commission_percent') {
        const v = parseFloat(value) || 0;
        return { ...prev, owner_commission_percent: v, company_commission_percent: 100 - v };
      }
      if (name === 'company_commission_percent') {
        const v = parseFloat(value) || 0;
        return { ...prev, company_commission_percent: v, owner_commission_percent: 100 - v };
      }
      return { ...prev, [name]: value };
    });
  };

  const handleSenderHubPercent = (hubId, value) => {
    setSenderHubPercents(prev => ({ ...prev, [hubId]: parseFloat(value) || 0 }));
  };

  const handleReceiverHubPercent = (hubId, value) => {
    setReceiverHubPercents(prev => ({ ...prev, [hubId]: parseFloat(value) || 0 }));
  };

  const toggleHubExpand = (key) => {
    setExpandedHubs(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const getCompanyCommissionAmount = () => {
    if (!preview) return 0;
    return (preview.total_freight * (parseFloat(formData.company_commission_percent) || 0)) / 100;
  };

  const calculateCompanyCommission = () => getCompanyCommissionAmount().toFixed(2);
  const calculateOwnerCommission = () => {
    if (!preview) return '0.00';
    return ((preview.total_freight * (parseFloat(formData.owner_commission_percent) || 0)) / 100).toFixed(2);
  };

  // Per-hub commission = (company commission amount) * (hub %) / 100
  const calcHubAmount = (percent) => {
    return (getCompanyCommissionAmount() * (parseFloat(percent) || 0)) / 100;
  };

  const getTotalSenderHubCommission = () => {
    return Object.values(senderHubPercents).reduce((sum, pct) => sum + calcHubAmount(pct), 0);
  };

  const getTotalReceiverHubCommission = () => {
    return Object.values(receiverHubPercents).reduce((sum, pct) => sum + calcHubAmount(pct), 0);
  };

  const filteredMlSheets = mlSheets.filter(s => s.ml_sheet_id.toLowerCase().includes(mlSearchQuery.toLowerCase()));
  const paginatedMlSheets = filteredMlSheets.slice(0, mlPage * ML_PAGE_SIZE);
  const hasMoreMl = paginatedMlSheets.length < filteredMlSheets.length;

  const handleMlSelect = (sheetId) => {
    handleChange({ target: { name: 'ml_sheet_id', value: sheetId } });
    setMlDropdownOpen(false);
    setMlSearchQuery('');
    setMlPage(1);
  };

  const handleMlScroll = (e) => {
    const el = e.target;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < 10 && hasMoreMl) setMlPage(p => p + 1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.ml_sheet_id) { Alert.error('Please select an ML Sheet'); return; }
    if (!formData.company_profile_id) { Alert.error('Please select a Company Profile'); return; }
    if (!formData.vehicle_owner_id) { Alert.error('Please select a Vehicle Owner'); return; }

    setLoading(true);
    try {
      const buildHubArray = (hubs, percents) =>
        (hubs || []).filter(h => (parseFloat(percents[h.hub_id]) || 0) > 0).map(h => ({
          hub_id: h.hub_id,
          hub_location: h.hub_location,
          commission_percent: parseFloat(percents[h.hub_id]) || 0,
        }));

      await commissionService.createCommission({
        ml_sheet_id: formData.ml_sheet_id,
        company_profile_id: formData.company_profile_id,
        vehicle_owner_id: formData.vehicle_owner_id,
        company_commission_percent: parseFloat(formData.company_commission_percent) || 30,
        owner_commission_percent: parseFloat(formData.owner_commission_percent) || 70,
        sender_hub_commissions: buildHubArray(hubData?.sender_hubs, senderHubPercents),
        receiver_hub_commissions: buildHubArray(hubData?.receiver_hubs, receiverHubPercents),
      });
      Alert.success('Commission created successfully!');
      setTimeout(() => navigate('/commissions'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create commission');
    } finally {
      setLoading(false);
    }
  };

  const senderHubs = hubData?.sender_hubs || [];
  const receiverHubs = hubData?.receiver_hubs || [];
  const totalSenderComm = getTotalSenderHubCommission();
  const totalReceiverComm = getTotalReceiverHubCommission();

  const renderHubRow = (hub, type, percents, setPercent) => {
    const key = `${type}-${hub.hub_id}`;
    const isExpanded = expandedHubs[key];
    const pct = percents[hub.hub_id] || 0;
    const amt = calcHubAmount(pct);
    const colorClass = type === 'sender' ? 'purple' : 'indigo';

    return (
      <div key={hub.hub_id} className="border-b border-gray-100 last:border-b-0">
        {/* Hub Row */}
        <div className="flex items-center gap-2 px-4 py-3 hover:bg-gray-50 transition-colors">
          {/* Expand toggle */}
          <button type="button" onClick={() => toggleHubExpand(key)} className="shrink-0 p-0.5">
            {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-400" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
          </button>
          <MapPin className={`w-4 h-4 text-${colorClass}-400 shrink-0`} />

          {/* Hub info */}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-secondary truncate">{hub.hub_location}</p>
            <p className="text-xs text-gray-400">{hub.hub_id} &middot; {hub.total_shipments} shipment{hub.total_shipments !== 1 ? 's' : ''} &middot; ₹{parseFloat(hub.total_freight || 0).toLocaleString('en-IN')}</p>
          </div>

          {/* Per-hub commission input */}
          <div className="flex items-center gap-2 shrink-0">
            <div className="w-20">
              <input
                type="number"
                value={pct}
                onChange={(e) => setPercent(hub.hub_id, e.target.value)}
                onFocus={(e) => { if (Number(e.target.value) === 0) e.target.select(); }}
                step="0.01" min="0" max="100" placeholder="0"
                className={`w-full px-2 py-1.5 border border-${colorClass}-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-${colorClass}-500 text-sm text-center bg-white`}
              />
            </div>
            <span className="text-xs text-gray-400 w-4">%</span>
            <div className={`w-24 px-2 py-1.5 bg-${colorClass}-50 border border-${colorClass}-200 rounded-sm text-sm font-bold text-${colorClass}-800 text-center`}>
              ₹{amt.toFixed(2)}
            </div>
          </div>
        </div>

        {/* Agent breakdown */}
        {isExpanded && hub.agents && hub.agents.length > 0 && (
          <div className="bg-gray-50 px-4 pb-3 pt-1 ml-8">
            <p className="text-xs font-semibold text-gray-500 mb-2">{type === 'sender' ? 'Agents under this hub:' : 'Users under this hub:'}</p>
            <div className="bg-white border border-gray-200 rounded-sm overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">{type === 'sender' ? 'Agent' : 'User'}</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-600">City</th>
                    {type === 'sender' && <th className="px-3 py-2 text-center font-semibold text-gray-600">Shipments</th>}
                    {type === 'sender' && <th className="px-3 py-2 text-right font-semibold text-gray-600">Freight</th>}
                    <th className="px-3 py-2 text-center font-semibold text-gray-600">Comm. %</th>
                    <th className="px-3 py-2 text-right font-semibold text-gray-600">Comm. Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {hub.agents.map((agent) => {
                    const agentPct = parseFloat(agent.agent_commission_percent || 0);
                    const agentAmt = (amt * agentPct) / 100;
                    return (
                      <tr key={agent.agent_id}>
                        <td className="px-3 py-2 text-secondary">{agent.agent_name} <span className="text-gray-400">({agent.agent_id})</span></td>
                        <td className="px-3 py-2 text-gray-500">{agent.agent_city || '-'}</td>
                        {type === 'sender' && <td className="px-3 py-2 text-center">{agent.booked_shipments}</td>}
                        {type === 'sender' && <td className="px-3 py-2 text-right font-semibold">₹{parseFloat(agent.total_freight || 0).toLocaleString('en-IN')}</td>}
                        <td className="px-3 py-2 text-center">{agentPct.toFixed(1)}%</td>
                        <td className="px-3 py-2 text-right font-semibold">₹{agentAmt.toFixed(2)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <PageLayout title="Add Commission" icon={DollarSign}>
      {dataLoading ? (
        <div className="bg-white rounded-card shadow-card p-8 text-center text-sm text-gray-500">Loading form data...</div>
      ) : (
        <form onSubmit={handleSubmit} className="bg-white rounded-card shadow-card p-4 sm:p-8 space-y-6 sm:space-y-8">

                    {/* Step 1: ML Sheet */}
                    <div>
                      <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 1: Select Shipment</h3>
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">ML Sheet ID <span className="text-danger font-bold">*</span></label>
                        <div className="relative ml-sheet-dropdown">
                          <div className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm text-sm sm:text-base cursor-pointer bg-white flex justify-between items-center"
                            onClick={() => { setMlDropdownOpen(p => !p); setMlPage(1); }}>
                            <span className={formData.ml_sheet_id ? 'text-gray-900' : 'text-gray-400'}>{formData.ml_sheet_id || 'Select ML Sheet'}</span>
                            <span className="text-gray-400 text-xs">▼</span>
                          </div>
                          {mlDropdownOpen && (
                            <div className="absolute z-50 w-full bg-white border border-gray-300 rounded-sm shadow-lg mt-1">
                              <div className="p-2 border-b border-gray-200">
                                <input type="text" placeholder="Search ML Sheet..." value={mlSearchQuery} onChange={(e) => { setMlSearchQuery(e.target.value); setMlPage(1); }}
                                  className="w-full px-3 py-1.5 border border-gray-300 rounded-sm text-sm focus:outline-none focus:ring-2 focus:ring-brand" autoFocus />
                              </div>
                              <div className="max-h-48 overflow-y-auto" onScroll={handleMlScroll}>
                                {paginatedMlSheets.length > 0 ? paginatedMlSheets.map(s => (
                                  <div key={s.ml_sheet_id} onClick={() => handleMlSelect(s.ml_sheet_id)}
                                    className={`px-4 py-2 text-sm cursor-pointer hover:bg-blue-50 hover:text-blue-700 ${formData.ml_sheet_id === s.ml_sheet_id ? 'bg-blue-100 font-semibold' : ''}`}>{s.ml_sheet_id}</div>
                                )) : <div className="px-4 py-3 text-sm text-gray-400">No ML Sheets found</div>}
                                {hasMoreMl && <div className="px-4 py-2 text-xs text-center text-gray-400 border-t border-gray-100">Scroll for more...</div>}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {mlLoading && <div className="text-center py-4 text-sm text-gray-500">Loading shipment data...</div>}

                    {/* Step 2: Shipment Details */}
                    {!mlLoading && formData.ml_sheet_id && preview && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 2: Shipment Details</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div className="bg-brand-light p-4 rounded-sm">
                            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Freight</label>
                            <p className="text-lg font-bold text-secondary">₹{parseFloat(preview.total_freight || 0).toLocaleString('en-IN')}</p>
                          </div>
                          <div className="bg-yellow-50 p-4 rounded-sm">
                            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-1">Total Recovery</label>
                            <p className="text-lg font-bold text-secondary">₹{parseFloat(preview.total_recovery || 0).toLocaleString('en-IN')}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 3: Vehicle Owner */}
                    {!mlLoading && formData.ml_sheet_id && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 3: Vehicle Owner</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Owner <span className="text-danger font-bold">*</span></label>
                            <select name="vehicle_owner_id" value={formData.vehicle_owner_id} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" required>
                              <option value="">Select Vehicle Owner</option>
                              {vehicleOwners.map(o => <option key={o.vehicle_owner_id} value={o.vehicle_owner_id}>{o.owner_name}</option>)}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Commission %</label>
                            <input type="number" name="owner_commission_percent" value={formData.owner_commission_percent} onChange={handleChange} min="0" max="100" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" />
                            <div className="w-full px-3 sm:px-4 py-2 bg-blue-50 border border-gray-300 rounded-sm text-sm sm:text-base font-semibold text-secondary">₹{preview ? calculateOwnerCommission() : '0.00'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 4: Company */}
                    {!mlLoading && formData.ml_sheet_id && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 4: Primary Agent</h3>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                          <div>
                            <label className="block text-xs sm:text-sm font-medium text-secondary mb-1 sm:mb-2">Company Profile <span className="text-danger font-bold">*</span></label>
                            <select name="company_profile_id" value={formData.company_profile_id} onChange={handleChange} className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" required>
                              <option value="">Select Company Profile</option>
                              {companyProfiles.map(c => <option key={c.company_profile_id} value={c.company_profile_id}>{c.company_name}</option>)}
                            </select>
                          </div>
                          <div className="space-y-2">
                            <label className="block text-xs sm:text-sm font-medium text-gray-600 mb-1 sm:mb-2">Commission %</label>
                            <input type="number" name="company_commission_percent" value={formData.company_commission_percent} onChange={handleChange} min="0" max="100" className="w-full px-3 sm:px-4 py-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-brand text-sm sm:text-base" />
                            <div className="w-full px-3 sm:px-4 py-2 bg-green-50 border border-gray-300 rounded-sm text-sm sm:text-base font-semibold text-secondary">₹{preview ? calculateCompanyCommission() : '0.00'}</div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Step 5: Location Hub Commission */}
                    {!mlLoading && formData.ml_sheet_id && formData.vehicle_owner_id && formData.company_profile_id && (
                      <div>
                        <h3 className="text-base sm:text-md font-semibold text-tertiary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Step 5: Location Hub Commission</h3>

                        <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 mb-5">
                          <p className="text-xs text-amber-800">
                            <span className="font-semibold">Note:</span> Hub commission is calculated as a percentage of the
                            <span className="font-bold"> Company Commission Amount (₹{preview ? calculateCompanyCommission() : '0.00'})</span>.
                            Set commission % individually for each location hub.
                          </p>
                        </div>

                        {/* Sender / Receiver Tabs */}
                        <div className="flex mb-0 border border-gray-200 rounded-t-sm overflow-hidden">
                          <button type="button" onClick={() => setActiveHubTab('sender')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-all ${activeHubTab === 'sender' ? 'bg-purple-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                            <Send className="w-4 h-4" /> Sender Hubs ({senderHubs.length})
                          </button>
                          <button type="button" onClick={() => setActiveHubTab('receiver')}
                            className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold transition-all ${activeHubTab === 'receiver' ? 'bg-indigo-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>
                            <Download className="w-4 h-4" /> Receiver Hubs ({receiverHubs.length})
                          </button>
                        </div>

                        {/* SENDER TAB */}
                        {activeHubTab === 'sender' && (
                          <div className="border border-t-0 border-purple-200 rounded-b-sm overflow-hidden">
                            {/* Header bar */}
                            <div className="bg-purple-50 px-4 py-2.5 border-b border-purple-200 flex items-center justify-between">
                              <p className="text-xs font-semibold text-purple-700">
                                Base: Company Commission = ₹{preview ? calculateCompanyCommission() : '0.00'}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-purple-600">Total Sender Commission:</span>
                                <span className="text-sm font-bold text-purple-800 bg-purple-100 px-2 py-0.5 rounded">₹{totalSenderComm.toFixed(2)}</span>
                              </div>
                            </div>

                            {/* Column header */}
                            {senderHubs.length > 0 && (
                              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2 text-xs font-semibold text-gray-500">
                                <div className="w-5"></div>
                                <div className="w-4"></div>
                                <div className="flex-1">Hub Location</div>
                                <div className="w-20 text-center">Comm. %</div>
                                <div className="w-4"></div>
                                <div className="w-24 text-center">Amount (₹)</div>
                              </div>
                            )}

                            {senderHubs.length > 0 ? (
                              senderHubs.map(hub => renderHubRow(hub, 'sender', senderHubPercents, handleSenderHubPercent))
                            ) : (
                              <div className="p-6 text-center text-sm text-gray-400">No sender hubs found for this ML Sheet</div>
                            )}
                          </div>
                        )}

                        {/* RECEIVER TAB */}
                        {activeHubTab === 'receiver' && (
                          <div className="border border-t-0 border-indigo-200 rounded-b-sm overflow-hidden">
                            <div className="bg-indigo-50 px-4 py-2.5 border-b border-indigo-200 flex items-center justify-between">
                              <p className="text-xs font-semibold text-indigo-700">
                                Base: Company Commission = ₹{preview ? calculateCompanyCommission() : '0.00'}
                              </p>
                              <div className="flex items-center gap-2">
                                <span className="text-xs text-indigo-600">Total Receiver Commission:</span>
                                <span className="text-sm font-bold text-indigo-800 bg-indigo-100 px-2 py-0.5 rounded">₹{totalReceiverComm.toFixed(2)}</span>
                              </div>
                            </div>

                            {receiverHubs.length > 0 && (
                              <div className="bg-gray-50 px-4 py-2 border-b border-gray-200 flex items-center gap-2 text-xs font-semibold text-gray-500">
                                <div className="w-5"></div>
                                <div className="w-4"></div>
                                <div className="flex-1">Hub Location</div>
                                <div className="w-20 text-center">Comm. %</div>
                                <div className="w-4"></div>
                                <div className="w-24 text-center">Amount (₹)</div>
                              </div>
                            )}

                            {receiverHubs.length > 0 ? (
                              receiverHubs.map(hub => renderHubRow(hub, 'receiver', receiverHubPercents, handleReceiverHubPercent))
                            ) : (
                              <div className="p-6 text-center text-sm text-gray-400">No receiver hubs found for this ML Sheet</div>
                            )}
                          </div>
                        )}

                        {/* Combined Preview */}
                        {(totalSenderComm > 0 || totalReceiverComm > 0) && preview && (
                          <div className="mt-5 bg-gray-50 border border-gray-200 rounded-sm p-4">
                            <p className="text-xs font-semibold text-gray-600 mb-3">Hub Commission Preview</p>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                              <div className="bg-purple-50 border border-purple-200 rounded-sm p-3 text-center">
                                <p className="text-xs text-purple-600 mb-1">Total Sender Hub Commission</p>
                                <p className="text-lg font-bold text-purple-800">₹{totalSenderComm.toFixed(2)}</p>
                                <p className="text-xs text-gray-400 mt-1">{Object.values(senderHubPercents).filter(v => v > 0).length} hub{Object.values(senderHubPercents).filter(v => v > 0).length !== 1 ? 's' : ''}</p>
                              </div>
                              <div className="bg-indigo-50 border border-indigo-200 rounded-sm p-3 text-center">
                                <p className="text-xs text-indigo-600 mb-1">Total Receiver Hub Commission</p>
                                <p className="text-lg font-bold text-indigo-800">₹{totalReceiverComm.toFixed(2)}</p>
                                <p className="text-xs text-gray-400 mt-1">{Object.values(receiverHubPercents).filter(v => v > 0).length} hub{Object.values(receiverHubPercents).filter(v => v > 0).length !== 1 ? 's' : ''}</p>
                              </div>
                              <div className="bg-blue-50 border border-blue-200 rounded-sm p-3 text-center">
                                <p className="text-xs text-blue-600 mb-1">Grand Total Hub Commission</p>
                                <p className="text-lg font-bold text-blue-800">₹{(totalSenderComm + totalReceiverComm).toFixed(2)}</p>
                                <p className="text-xs text-gray-400 mt-1">from Company Comm. ₹{calculateCompanyCommission()}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
            <Button type="submit" loading={loading}>
              {loading ? 'Creating...' : 'Create Commission'}
            </Button>
            <Button type="button" variant="outline" onClick={() => navigate('/commissions')}>
              Cancel
            </Button>
          </div>
        </form>
      )}
    </PageLayout>
  );
};

export default AddCommission;
